import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import StatCard from '../components/dashboard/StatCard';
import StatFilterBar from '../components/dashboard/StatFilterBar';
import { useVersionInfo } from '../hooks/useVersionInfo';
import {
  QUICK_RANGE_KEYS,
  formatDateInput,
  resolveQuickRange,
} from '../utils/dateRange';

const COUNT_CARDS = [
  { key: 'registerNum', label: '註冊人數' },
  { key: 'depositNum', label: '充值人數' },
  { key: 'withdrawalNum', label: '提現人數' },
  { key: 'orderNum', label: '下單人數' },
  { key: 'firstDepositNum', label: '首存人數' },
  { key: 'secondDepositNum', label: '二存人數' },
  { key: 'thirdDepositNum', label: '三存人數' },
  { key: 'frontRegisterNum', label: '新增好友數' },
];

const AMOUNT_CARDS = [
  { key: 'totalOrderPrice', label: '下單總額' },
  { key: 'totalOrderFeePrice', label: '下單手續費總額' },
  { key: 'totalProfit', label: '盈虧總額' },
  { key: 'totalDeposit', label: '充值總額(USDT)' },
  { key: 'totalFirstDepositAmount', label: '首存金額(USDT)' },
  { key: 'totalSecondDepositAmount', label: '二存金額(USDT)' },
  { key: 'totalThirdDepositAmount', label: '三存金額(USDT)' },
  { key: 'totalWithdrawal', label: '提現總額(USDT)' },
  { key: 'totalRealWithdrawal', label: '實際提現總額(USDT)' },
  { key: 'systemModifyAmount', label: '系統加扣款' },
  { key: 'totalAwardAmount', label: '總客損(USDT)' },
  { key: 'totalBalance', label: '帳戶總額' },
  { key: 'yuebaoTotalAmount', label: '保管庫總額' },
  { key: 'lockedTotalAmount', label: '鎖倉總額' },
  { key: 'totalWithdrawalFee', label: '提現手續費' },
];

function formatCount(value) {
  const num = Number(value ?? 0);
  return num.toLocaleString('zh-TW');
}

function formatAmount(value) {
  const num = Number(value ?? 0);
  return num.toLocaleString('zh-TW', {
    minimumFractionDigits: 6,
    maximumFractionDigits: 6,
  });
}

const EMPTY_STATS = {};
COUNT_CARDS.forEach((item) => {
  EMPTY_STATS[item.key] = 0;
});
AMOUNT_CARDS.forEach((item) => {
  EMPTY_STATS[item.key] = '0.000000';
});

export default function DashboardPage() {
  const todayRange = useMemo(() => resolveQuickRange(QUICK_RANGE_KEYS.TODAY), []);
  const [startDate, setStartDate] = useState(() => formatDateInput(todayRange.start));
  const [endDate, setEndDate] = useState(() => formatDateInput(todayRange.end));
  const [activeQuick, setActiveQuick] = useState(QUICK_RANGE_KEYS.TODAY);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const versionText = useVersionInfo();

  const loadStats = useCallback(async (start, end) => {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.statsDailyReportIndex({ start, end });
      setStats({ ...EMPTY_STATS, ...data });
    } catch (err) {
      setError(err.message || '統計載入失敗');
      setStats(EMPTY_STATS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats(startDate, endDate);
    // 僅首次進入首頁載入今日統計
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleQuickSelect(key) {
    const range = resolveQuickRange(key);
    const start = formatDateInput(range.start);
    const end = formatDateInput(range.end);
    setActiveQuick(key);
    setStartDate(start);
    setEndDate(end);
    loadStats(start, end);
  }

  function handleSearch() {
    setActiveQuick('');
    loadStats(startDate, endDate);
  }

  function handleReset() {
    handleQuickSelect(QUICK_RANGE_KEYS.TODAY);
  }

  function handleRefresh() {
    loadStats(startDate, endDate);
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div className="dashboard-title-row">
          <h2>今日統計</h2>
          <button
            type="button"
            className="btn btn-secondary btn-sm dashboard-refresh"
            onClick={handleRefresh}
            disabled={loading}
          >
            刷新
          </button>
        </div>
        <span className="dashboard-version">版本號：{versionText}</span>
      </div>

      <StatFilterBar
        startDate={startDate}
        endDate={endDate}
        activeQuick={activeQuick}
        loading={loading}
        onStartChange={setStartDate}
        onEndChange={setEndDate}
        onQuickSelect={handleQuickSelect}
        onSearch={handleSearch}
        onReset={handleReset}
      />

      {error && <div className="alert alert-error">{error}</div>}
      {loading && <p className="dashboard-loading">載入中…</p>}

      <div className="stat-grid">
        {COUNT_CARDS.map((item) => (
          <StatCard
            key={item.key}
            label={item.label}
            value={formatCount(stats[item.key])}
            variant="count"
          />
        ))}
      </div>

      <div className="stat-grid">
        {AMOUNT_CARDS.map((item) => (
          <StatCard
            key={item.key}
            label={item.label}
            value={formatAmount(stats[item.key])}
            variant="amount"
          />
        ))}
      </div>
    </div>
  );
}
